export const formatLocalTime = (utcTimestamp: number, type = "DD/MM/YYYY") => {
  const date = new Date(utcTimestamp * 1000);
  let hours = date.getHours();
  hours = hours === 24 ? 0 : hours;
  const time = `${hours.toString().padStart(2, "0")}:${date
    .getMinutes()
    .toString()
    .padStart(2, "0")}`;

  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();

  if (type === "DD/MM/YYYY h:mm") {
    return `${day}/${month}/${year} ${time}`;
  } else if (type === "h:mm DD/MM") {
    return `${time} ${day}/${month}`;
  } else if (type === "DD/MM h:mm") {
    return `${day}/${month} ${time}`;
  } else if (type === "DD/MM") {
    return `${day}/${month}`;
  } else if (type === "MM/YYYY") {
    return `${month}/${year}`;
  } else if (type === "YYYY") {
    return `${year}`;
  }

  return `${day}/${month}/${year}`;
};

export const calculateAge = (timestamp: number): number => {
  if (!timestamp) return 0;

  const birthDate = new Date(timestamp * 1000);
  const today = new Date();

  const age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  // Nếu chưa tới tháng sinh nhật hoặc
  // đang đúng tháng nhưng chưa tới ngày sinh nhật thì trừ 1
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    return age - 1;
  }

  return age;
};

export const formatNumberToShort = (num: number): string => {
  if (num >= 1_000_000_000) return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B';
  if (num >= 1_000_000)     return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (num >= 1_000)         return (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return num.toString();
}

// string to slug
function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function stringToSlug(input: string): string {
  if (!input) return "";

  const CUSTOM_MAP: Record<string, string> = {
    "ə": "e", "Ə": "e",
    "ş": "s", "Ş": "s",
    "ı": "i", "İ": "i",
    "ç": "c", "Ç": "c",
    "ğ": "g", "Ğ": "g",
    "ö": "o", "Ö": "o",
    "ü": "u", "Ü": "u",
    "ḍ": "d", "Ḍ": "D",
    "ḥ": "h", "Ḥ": "H",
    "ṭ": "t", "Ṭ": "T",
    "ḷ": "l", "Ḷ": "L",
    "ṅ": "n", "Ṅ": "N",
    "ñ": "n", "Ñ": "N",
    "ṇ": "n", "Ṇ": "N",
    "ś": "s", "Ś": "S",
    "ṣ": "s", "Ṣ": "S",
    "ṛ": "r", "Ṛ": "R",
    "ã": "a", "á": "a",
    "à": "a", "â": "a",
    "ä": "a",
    // "ç": "c",
    "œ": "oe", "Œ": "OE",
    "ø": "o", "Ø": "O",
    "ǧ": "g", "Ǧ": "G",
    "ž": "z", "Ž": "Z",
    "–": "-",  // en-dash
    "—": "-",  // em-dash
    "（": "", "）": "",
    "“": "", "”": "",
    "‘": "", "’": "",
    "«": "", "»": "",
    "…": "",

    // Cyrillic mapping (Russian)
    "а": "a", "А": "A",
    "б": "b", "Б": "B",
    "в": "v", "В": "V",
    "г": "g", "Г": "G",
    "д": "d", "Д": "D",
    "е": "e", "Е": "E",
    "ё": "e", "Ё": "E",
    "ж": "zh","Ж": "Zh",
    "з": "z", "З": "Z",
    "и": "i", "И": "I",
    "й": "y", "Й": "Y",
    "к": "k", "К": "K",
    "л": "l", "Л": "L",
    "м": "m", "М": "M",
    "н": "n", "Н": "N",
    "о": "o", "О": "O",
    "п": "p", "П": "P",
    "р": "r", "Р": "R",
    "с": "s", "С": "S",
    "т": "t", "Т": "T",
    "у": "u", "У": "U",
    "ф": "f", "Ф": "F",
    "х": "kh","Х": "Kh",
    "ц": "ts","Ц": "Ts",
    "ч": "ch","Ч": "Ch",
    "ш": "sh","Ш": "Sh",
    "щ": "shch","Щ": "Shch",
    "ъ": "", "Ъ": "",
    "ы": "y", "Ы": "Y",
    "ь": "", "Ь": "",
    "э": "e", "Э": "E",
    "ю": "yu","Ю": "Yu",
    "я": "ya","Я": "Ya",
  };

  const CUSTOM_MAP_REGEX = new RegExp(
    Object.keys(CUSTOM_MAP).map(escapeRegExp).join("|"),
    "gu"
  );

  // 1) Thay theo custom map
  let s = input.replace(CUSTOM_MAP_REGEX, (m) => CUSTOM_MAP[m] ?? "");

  // 2) Loại invisible chars (U+200B..U+200D, U+2060, U+FEFF)
  s = s.replace(/[\u200B-\u200D\u2060\uFEFF]/g, "");

  // 3) Chuẩn hoá & bỏ dấu (NFD + bỏ các ký tự tổ hợp)
  //    Cần target ES2018+ để dùng \p{M}
  s = s.normalize("NFD").replace(/\p{M}+/gu, "");

  // 4) Hạ chữ thường
  s = s.toLowerCase();

  // 5) Bỏ HTML tags nếu có (đơn giản)
  s = s.replace(/<[^>]*>/g, "");

  // 6) Đổi mọi chuỗi ký tự không phải a-z0-9 thành dấu gạch ngang
  s = s.replace(/[^a-z0-9]+/g, "-");

  // 7) Gom & trim dấu gạch ngang
  s = s.replace(/-+/g, "-").replace(/^-+|-+$/g, "");

  return s;
}
